import React from 'react';

interface StepMeta {
  label: string;
}

interface OperationLayoutProps {
  title: string;
  description?: string;
  steps: StepMeta[];
  currentStep: number;
  error?: string | null;
  success?: string | null;
  children: React.ReactNode;
  icon?: React.ReactNode;
  accentColor?: string;
}

/**
 * Shared layout wrapper for all operation pages (Recargas, Paquetes, Facturas, Seguros).
 * Provides consistent header, step indicator, alert banners, and card body.
 */
const OperationLayout: React.FC<OperationLayoutProps> = ({
  title, description, steps, currentStep, error, success, children, icon,
  accentColor = '#2563eb',
}) => {
  const totalSteps = steps.length;

  return (
    <div className="p-5 max-w-3xl mx-auto">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 mb-6 animate-fadeIn">
        {icon && (
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0 text-white"
            style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, boxShadow: `0 4px 16px ${accentColor}44` }}>
            {icon}
          </div>
        )}
        <div>
          <h1 className="text-xl font-black text-slate-800">{title}</h1>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>

      {/* ── Step indicator ──────────────────────────────────────────── */}
      <div className="relative mb-6 animate-fadeIn">
        {/* Connecting line */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-slate-200" style={{ zIndex: 0 }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{
              width: totalSteps > 1 ? `${((currentStep - 1) / (totalSteps - 1)) * 100}%` : '0%',
              background: `linear-gradient(90deg, ${accentColor}, ${accentColor}aa)`,
            }} />
        </div>

        <div className="relative flex justify-between" style={{ zIndex: 1 }}>
          {steps.map((s, i) => {
            const idx = i + 1;
            const done    = currentStep > idx;
            const active  = currentStep === idx;
            const pending = currentStep < idx;
            return (
              <div key={idx} className="flex flex-col items-center gap-1.5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                  style={{
                    background: done ? '#10b981' : active ? accentColor : '#e2e8f0',
                    color: (done || active) ? '#fff' : '#94a3b8',
                    boxShadow: active ? `0 0 0 4px ${accentColor}33` : done ? '0 0 0 4px #10b98133' : 'none',
                  }}>
                  {done
                    ? <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    : idx}
                </div>
                <span className="text-xs font-semibold" style={{ color: active ? accentColor : done ? '#10b981' : '#94a3b8' }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Alerts ──────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 animate-slideIn"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p className="text-sm font-semibold text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 animate-slideIn"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p className="text-sm font-semibold text-emerald-700">{success}</p>
        </div>
      )}

      {/* ── Card body ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-card p-6 animate-scaleIn">
        {children}
      </div>
    </div>
  );
};

export default OperationLayout;
